/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Users and seats (v2): everyone in the tenant with their role, teams and a
 * column per seat add-on the tenant bought. Seats switch on and off in the
 * row; selecting people opens a bar to assign or remove seats, add them to a
 * team, set a role, or activate/deactivate them. A row opens the user page.
 *
 * Data: GET /users/directory. Writes: the bulk seat endpoints and
 * PUT /users/{id}/access, which apply all-or-nothing on the server.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import Snackbar from "@mui/material/Snackbar";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ChevronDown, Download, Search, UserPlus, X } from "lucide-react";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";
import { useCapability } from "auth/usePermission";
import { SettingsSection, ToneChip } from "components/v2/settings/SettingsBlocks";
import { v2 as t } from "styles/tokens/v2";
import { relativeTime } from "layouts/crm/account360/format";
import {
  STATUS,
  apiErrorText,
  filterUsers,
  fullName,
  initials,
  planSeatAssign,
  roleSummary,
  seatCounters,
  seatName,
} from "./usersModel";

const PAGE = 25;

function FilterSelect({ label, value, onChange, children }) {
  return (
    <TextField select size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 150 }}>
      {children}
    </TextField>
  );
}
FilterSelect.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, onChange: PropTypes.func.isRequired, children: PropTypes.node.isRequired };

function BulkMenuButton({ label, disabled, children }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Button size="small" variant="outlined" disabled={disabled} endIcon={<ChevronDown size={14} />} onClick={(e) => setAnchor(e.currentTarget)} sx={{ bgcolor: t.surface }}>
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} onClick={() => setAnchor(null)}>
        {children}
      </Menu>
    </>
  );
}
BulkMenuButton.propTypes = { label: PropTypes.string.isRequired, disabled: PropTypes.bool, children: PropTypes.node.isRequired };
BulkMenuButton.defaultProps = { disabled: false };

export default function UsersAndSeatsV2({ reloadToken, onInvite, onDownloadAllLogs }) {
  const { axiosInstance, user: me } = useAuth();
  const navigate = useNavigate();
  const canManage = useCapability("MANAGE_USERS");
  const canRoles = useCapability("MANAGE_ROLES");
  const canTeams = useCapability("MANAGE_TEAMS");

  const [data, setData] = useState({ users: [], seat_modules: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filters, setFilters] = useState({ query: "", roleId: "", teamId: "", seat: "", status: "all" });
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data: d } = await axiosInstance.get("/users/directory");
      setData({ users: d.users || [], seat_modules: d.seat_modules || [] });
    } catch (e) {
      setLoadError(apiErrorText(e, "Could not load users."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  useEffect(() => {
    axiosInstance.get("/users/assignable-roles").then(({ data: r }) => setRoles(r || [])).catch(() => setRoles([]));
    axiosInstance.get("/teams").then(({ data: tm }) => setTeams(tm || [])).catch(() => setTeams([]));
  }, [axiosInstance]);

  const { users, seat_modules: seatModules } = data;
  const rows = useMemo(
    () => filterUsers(users, { ...filters, roleId: filters.roleId || null, teamId: filters.teamId || null }).sort((a, b) => fullName(a).localeCompare(fullName(b))),
    [users, filters]
  );
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE));
  const current = Math.min(page, pageCount - 1);
  const visible = rows.slice(current * PAGE, current * PAGE + PAGE);
  const chosen = users.filter((u) => selected.includes(u.id));
  const baseRoles = roles.filter((r) => r.role_kind === "base");
  const addonRoles = roles.filter((r) => r.role_kind !== "base");

  const setFilter = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(0);
  };
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allVisibleSelected = visible.length > 0 && visible.every((u) => selected.includes(u.id));
  const toggleVisible = () =>
    setSelected((s) => (allVisibleSelected ? s.filter((id) => !visible.some((u) => u.id === id)) : [...new Set([...s, ...visible.map((u) => u.id)])]));

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      setToast({ severity: "success", text: okText });
      await load();
      return true;
    } catch (e) {
      setToast({ severity: "error", text: apiErrorText(e) });
      await load();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const assignSeats = (module) => {
    const plan = planSeatAssign(users, selected, module);
    if (!plan.toAssign.length) {
      setToast({ severity: "info", text: `Everyone selected who can have a ${seatName(module)} seat already has one.` });
      return;
    }
    const left = Math.max(0, module.seats_remaining);
    if (plan.shortfall) {
      setToast({
        severity: "error",
        text: `${plan.toAssign.length === 1 ? "1 person needs" : `${plan.toAssign.length} people need`} a ${seatName(module)} seat but ${left === 0 ? "none are" : left === 1 ? "only 1 is" : `only ${left} are`} left. ${left ? `Remove ${plan.shortfall} from the selection or buy more seats.` : "Buy more seats or free some up first."}`,
      });
      return;
    }
    const skipped = plan.ineligible.length ? ` ${plan.ineligible.length} skipped (inactive or support users).` : "";
    run(
      () => axiosInstance.post(`/license/module-seats/${module.module_code}/assign-bulk`, { user_ids: plan.toAssign.map((u) => u.id) }),
      `${seatName(module)} seat given to ${plan.toAssign.length} ${plan.toAssign.length === 1 ? "person" : "people"}.${skipped}`
    ).then((ok) => ok && setSelected([]));
  };

  const removeSeats = (module) => {
    const holders = chosen.filter((u) => u.seats?.[module.module_code]);
    if (!holders.length) {
      setToast({ severity: "info", text: `Nobody selected has a ${seatName(module)} seat.` });
      return;
    }
    run(
      () => axiosInstance.post(`/license/module-seats/${module.module_code}/revoke-bulk`, { user_ids: holders.map((u) => u.id) }),
      `${seatName(module)} seat removed from ${holders.length} ${holders.length === 1 ? "person" : "people"}.`
    ).then((ok) => ok && setSelected([]));
  };

  // Per-person changes go through the access endpoint one by one, so a
  // failure names the person it stopped at.
  const eachUser = async (people, bodyFor, verb) => {
    let done = 0;
    setBusy(true);
    try {
      for (const u of people) {
        const body = bodyFor(u);
        if (body) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await axiosInstance.put(`/users/${u.id}/access`, body);
            done += 1;
          } catch (e) {
            setToast({ severity: "error", text: `${verb} stopped at ${fullName(u)}: ${apiErrorText(e)} ${done ? `${done} already done.` : ""}` });
            await load();
            return;
          }
        }
      }
      setToast({ severity: "success", text: `${verb}: ${done} ${done === 1 ? "person" : "people"} updated.` });
      setSelected([]);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const addToTeam = (team) =>
    eachUser(
      chosen,
      (u) =>
        (u.teams || []).some((tm) => tm.id === team.id)
          ? null
          : { teams: [...(u.teams || []).map((tm) => ({ team_id: tm.id, membership: tm.membership || "member", is_primary: Boolean(tm.is_primary) })), { team_id: team.id, membership: "member", is_primary: !(u.teams || []).length }] },
      `Added to ${team.name}`
    );
  const setBaseRole = (role) => eachUser(chosen, (u) => (u.base_role?.id === role.id ? null : { base_role_id: role.id }), `Role set to ${role.name}`);
  const addAddonRole = (role) =>
    eachUser(
      chosen,
      (u) => ((u.addon_roles || []).some((r) => r.id === role.id) ? null : { addon_role_ids: [...(u.addon_roles || []).map((r) => r.id), role.id] }),
      `${role.name} added`
    );
  const setActive = (active) =>
    eachUser(
      chosen.filter((u) => u.id !== me?.id),
      (u) => (u.is_active === active ? null : { is_active: active }),
      active ? "Activated" : "Deactivated"
    );

  const toggleSeat = (u, module) => {
    const on = !u.seats?.[module.module_code];
    // Flip it now; the reload after the save (or its failure) settles it.
    setData((d) => ({ ...d, users: d.users.map((x) => (x.id === u.id ? { ...x, seats: { ...x.seats, [module.module_code]: on } } : x)) }));
    run(
      () => axiosInstance.put(`/users/${u.id}/access`, { seats: { [module.module_code]: on } }),
      `${seatName(module)} seat ${on ? "given to" : "removed from"} ${fullName(u)}.`
    );
  };

  const seatsUsedLine = seatCounters(seatModules);
  const activeCount = users.filter((u) => u.status === "active").length;
  const invitedCount = users.filter((u) => u.status === "invited").length;

  const headCell = { fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: t.text3, px: 1.5, py: 1.25, textAlign: "left", whiteSpace: "nowrap" };
  const cell = { px: 1.5, py: 1.25, borderTop: `1px solid ${t.border}`, fontSize: 14, color: t.text, verticalAlign: "middle" };

  return (
    <SettingsSection
      title="Users and seats"
      summary={[`${activeCount} active`, invitedCount ? `${invitedCount} invited` : null, seatsUsedLine].filter(Boolean).join(" · ")}
      actions={
        <>
          <MDButton variant="outlined" color="dark" startIcon={<Download size={16} />} onClick={onDownloadAllLogs}>
            Download all logs
          </MDButton>
          {canManage && (
            <MDButton variant="gradient" color="info" startIcon={<UserPlus size={16} />} onClick={onInvite}>
              Invite user
            </MDButton>
          )}
        </>
      }
      flush
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, px: 2.75, pb: 1.75 }}>
        <TextField
          size="small"
          placeholder="Search name, email, title"
          value={filters.query}
          onChange={(e) => setFilter("query", e.target.value)}
          inputProps={{ "aria-label": "Search users" }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} color={t.text3} /></InputAdornment> }}
          sx={{ width: { xs: "100%", sm: 260 } }}
        />
        <FilterSelect label="Role" value={filters.roleId} onChange={(v) => setFilter("roleId", v)}>
          <MenuItem value="">Any role</MenuItem>
          {roles.map((r) => (
            <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
          ))}
        </FilterSelect>
        <FilterSelect label="Team" value={filters.teamId} onChange={(v) => setFilter("teamId", v)}>
          <MenuItem value="">Any team</MenuItem>
          {teams.map((tm) => (
            <MenuItem key={tm.id} value={tm.id}>{tm.name}</MenuItem>
          ))}
        </FilterSelect>
        {seatModules.length > 0 && (
          <FilterSelect label="Seat" value={filters.seat} onChange={(v) => setFilter("seat", v)}>
            <MenuItem value="">Any seat</MenuItem>
            {seatModules.flatMap((m) => [
              <MenuItem key={`${m.module_code}:yes`} value={`${m.module_code}:yes`}>Has {seatName(m)}</MenuItem>,
              <MenuItem key={`${m.module_code}:no`} value={`${m.module_code}:no`}>No {seatName(m)}</MenuItem>,
            ])}
          </FilterSelect>
        )}
        <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilter("status", v)}>
          <MenuItem value="all">Any status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="invited">Invited</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </FilterSelect>
        <Typography sx={{ ml: "auto", fontSize: 14, color: t.text2 }}>
          {rows.length} of {users.length}
        </Typography>
      </Box>

      {selected.length > 0 && canManage && (
        <Box
          role="toolbar"
          aria-label="Change the selected users"
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, px: 2.75, py: 1.25, bgcolor: "#E6EFFB", borderTop: `1px solid ${t.border}` }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1453A0", mr: 1 }}>{selected.length} selected</Typography>
          {seatModules.length > 0 && (
            <>
              <BulkMenuButton label="Assign seat" disabled={busy}>
                {seatModules.map((m) => (
                  <MenuItem key={m.module_code} onClick={() => assignSeats(m)}>
                    {seatName(m)}
                    <Typography component="span" sx={{ ml: 1.5, fontSize: 13, color: t.text2 }}>{Math.max(0, m.seats_remaining)} left</Typography>
                  </MenuItem>
                ))}
              </BulkMenuButton>
              <BulkMenuButton label="Remove seat" disabled={busy}>
                {seatModules.map((m) => (
                  <MenuItem key={m.module_code} onClick={() => removeSeats(m)}>{seatName(m)}</MenuItem>
                ))}
              </BulkMenuButton>
            </>
          )}
          {canTeams && teams.length > 0 && (
            <BulkMenuButton label="Add to team" disabled={busy}>
              {teams.map((tm) => (
                <MenuItem key={tm.id} onClick={() => addToTeam(tm)}>{tm.name}</MenuItem>
              ))}
            </BulkMenuButton>
          )}
          {canRoles && roles.length > 0 && (
            <BulkMenuButton label="Roles" disabled={busy}>
              <ListSubheader>Set base role</ListSubheader>
              {baseRoles.map((r) => (
                <MenuItem key={r.id} onClick={() => setBaseRole(r)}>{r.name}</MenuItem>
              ))}
              {addonRoles.length > 0 && <ListSubheader>Add an add-on role</ListSubheader>}
              {addonRoles.map((r) => (
                <MenuItem key={r.id} onClick={() => addAddonRole(r)}>{r.name}</MenuItem>
              ))}
            </BulkMenuButton>
          )}
          <Button size="small" variant="outlined" disabled={busy} onClick={() => setActive(true)} sx={{ bgcolor: t.surface }}>Activate</Button>
          <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => setActive(false)} sx={{ bgcolor: t.surface }}>Deactivate</Button>
          {busy && <CircularProgress size={18} sx={{ ml: 1 }} />}
          <Button size="small" startIcon={<X size={14} />} onClick={() => setSelected([])} sx={{ ml: "auto" }}>Clear</Button>
        </Box>
      )}

      {loadError && <Alert severity="error" sx={{ mx: 2.75, mb: 2 }}>{loadError}</Alert>}

      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
          <thead>
            <tr>
              <Box component="th" sx={{ ...headCell, width: 44, pl: 2 }}>
                {canManage && (
                  <Checkbox size="small" checked={allVisibleSelected} indeterminate={!allVisibleSelected && visible.some((u) => selected.includes(u.id))} onChange={toggleVisible} inputProps={{ "aria-label": "Select everyone on this page" }} />
                )}
              </Box>
              <Box component="th" scope="col" sx={headCell}>User</Box>
              <Box component="th" scope="col" sx={headCell}>Role</Box>
              <Box component="th" scope="col" sx={headCell}>Teams</Box>
              {seatModules.map((m) => (
                <Box component="th" scope="col" key={m.module_code} sx={headCell}>
                  {seatName(m)}
                  <Box component="span" sx={{ display: "block", fontSize: 11.5, fontWeight: 500, letterSpacing: 0, textTransform: "none", color: t.text3 }}>
                    {m.used_seats} of {m.total_seats}
                  </Box>
                </Box>
              ))}
              <Box component="th" scope="col" sx={headCell}>Status</Box>
              <Box component="th" scope="col" sx={headCell}>Last login</Box>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => {
              const status = STATUS[u.status] || STATUS.active;
              const isSelected = selected.includes(u.id);
              return (
                <Box component="tr" key={u.id} sx={{ bgcolor: isSelected ? "#F3F7FD" : undefined, "&:hover": { bgcolor: isSelected ? "#EAF1FB" : t.hover } }}>
                  <Box component="td" sx={{ ...cell, pl: 2 }}>
                    {canManage && <Checkbox size="small" checked={isSelected} onChange={() => toggleOne(u.id)} inputProps={{ "aria-label": `Select ${fullName(u)}` }} />}
                  </Box>
                  <Box component="td" sx={cell}>
                    <ButtonBase onClick={() => navigate(`/settings/users/${u.id}`)} sx={{ display: "flex", alignItems: "center", gap: 1.25, fontFamily: "inherit", textAlign: "left" }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, bgcolor: "#E6EFFB", color: "#1453A0", flexShrink: 0 }}>
                        {initials(u)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: t.link }}>{fullName(u)}</Typography>
                        <Typography sx={{ fontSize: 13, color: t.text2 }}>{u.email}{u.designation ? ` · ${u.designation}` : ""}</Typography>
                      </Box>
                    </ButtonBase>
                  </Box>
                  <Box component="td" sx={cell}>
                    <Tooltip title={(u.addon_roles || []).map((r) => r.name).join(", ")} disableHoverListener={!(u.addon_roles || []).length}>
                      <span>{roleSummary(u)}</span>
                    </Tooltip>
                  </Box>
                  <Box component="td" sx={{ ...cell, color: (u.teams || []).length ? t.text : t.text3 }}>
                    {(u.teams || []).map((tm) => tm.name).join(", ") || "None"}
                  </Box>
                  {seatModules.map((m) => {
                    const on = Boolean(u.seats?.[m.module_code]);
                    const blocked = !on && (m.seats_remaining <= 0 || u.status === "inactive" || u.is_support_user);
                    const why = !on && m.seats_remaining <= 0 ? `No ${seatName(m)} seats left` : !on && u.status === "inactive" ? "Activate this user first" : "";
                    return (
                      <Box component="td" key={m.module_code} sx={cell}>
                        <Tooltip title={why} disableHoverListener={!why}>
                          <span>
                            <Switch
                              size="small"
                              checked={on}
                              disabled={!canManage || busy || blocked}
                              onChange={() => toggleSeat(u, m)}
                              inputProps={{ "aria-label": `${seatName(m)} seat for ${fullName(u)}` }}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    );
                  })}
                  <Box component="td" sx={cell}>
                    <ToneChip tone={status.tone}>{status.label}</ToneChip>
                  </Box>
                  <Box component="td" sx={{ ...cell, color: t.text2, whiteSpace: "nowrap" }}>{u.last_login ? relativeTime(u.last_login) : "Never"}</Box>
                </Box>
              );
            })}
          </tbody>
        </Box>
      </Box>
      {loading && (
        <Box sx={{ py: 5, display: "grid", placeItems: "center" }}>
          <CircularProgress size={26} />
        </Box>
      )}
      {!loading && !visible.length && !loadError && (
        <Typography sx={{ py: 5, textAlign: "center", fontSize: 14, color: t.text2 }}>No users match these filters.</Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.75, py: 1.25, borderTop: `1px solid ${t.border}` }}>
        <Typography sx={{ fontSize: 13.5, color: t.text2 }}>
          {rows.length ? `Showing ${current * PAGE + 1}–${Math.min(rows.length, current * PAGE + PAGE)} of ${rows.length}` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="outlined" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</Button>
          <Button size="small" variant="outlined" disabled={current >= pageCount - 1} onClick={() => setPage(current + 1)}>Next</Button>
        </Box>
      </Box>

      <Snackbar open={Boolean(toast)} autoHideDuration={7000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled" sx={{ maxWidth: 640 }}>
            {toast.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </SettingsSection>
  );
}

UsersAndSeatsV2.propTypes = {
  // Changes when the page around it creates or changes a user, to reload.
  reloadToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onInvite: PropTypes.func.isRequired,
  onDownloadAllLogs: PropTypes.func.isRequired,
};
UsersAndSeatsV2.defaultProps = { reloadToken: 0 };
